/**
 * MBA Lab — the Leadership Practice Snapshot's 27 items and every piece of
 * readout prose (strength/growth text, quadrant descriptions, disclaimers).
 * Pure data, no functions: snapshot-model.js reads this and the Razor view
 * never hardcodes item/readout copy, matching the planner-modes.js
 * precedent of keeping every piece of editorial copy in one place a
 * copy-editor can find without touching the state-machine logic in
 * snapshot-model.js.
 *
 * Three domains, re-derived (not ported) from three well-documented areas
 * of self-awareness: how you carry the Task/Others/Self balance under
 * pressure, how your own ego patterns show up, and how you actually behave
 * in disagreement. Every item below is an original, scenario-anchored,
 * observable-behavior statement written for this tool. None of the
 * wording, scale, or scoring is copied or adapted from any licensed
 * instrument (MSCEIT, EQ-i 2.0, MAAS/FFMQ, Gallup Q12, the Edmondson
 * psychological-safety scale, or any commercial four-style behavioral-
 * styles instrument or virtues-based instrument - see the source report's
 * Table 16 for the full licensed-instrument list this deliberately
 * avoids). This is an original,
 * unvalidated, educational self-reflection tool - see
 * /leadership/leadership-assessment-validation-roadmap for what turning it
 * into a validated instrument would actually require.
 *
 * The output is prose, never a percentage or an identity label: buildReadout()
 * names specific answered behaviors as a strength or a growth edge, and
 * classifies the care/accountability overlay as a *pattern in today's answers*
 * (using the same quadrant names as the published care/accountability matrix
 * graphic), never as a claim about who the person permanently is.
 */

export const DOMAINS = Object.freeze({
  SELF_AWARENESS: 'self-awareness',
  EGO_PATTERNS: 'ego-patterns',
  CONFLICT_RESPONSE: 'conflict-response',
});

export const DOMAIN_ORDER = [DOMAINS.SELF_AWARENESS, DOMAINS.EGO_PATTERNS, DOMAINS.CONFLICT_RESPONSE];

export const DOMAIN_LABELS = Object.freeze({
  [DOMAINS.SELF_AWARENESS]: 'Self-Awareness Under Pressure',
  [DOMAINS.EGO_PATTERNS]: 'Ego Patterns',
  [DOMAINS.CONFLICT_RESPONSE]: 'Conflict Response',
});

// A 5-point observable-behavior frequency scale, not an agreement/trait scale -
// every item asks how often something was actually done, not whether a
// personality statement feels true.
export const FREQUENCY_OPTIONS = Object.freeze([
  { value: 1, label: 'Rarely or never' },
  { value: 2, label: 'Occasionally' },
  { value: 3, label: 'Sometimes' },
  { value: 4, label: 'Often' },
  { value: 5, label: 'Consistently' },
]);

export const AXIS = Object.freeze({ CARE: 'care', ACCOUNTABILITY: 'accountability' });

export const ITEMS = Object.freeze([
  // --- Self-Awareness Under Pressure ---
  { id: 'sa-1', domain: DOMAINS.SELF_AWARENESS, axis: null,
    prompt: 'I state clearly what a good outcome looks like before work starts, rather than letting it stay vague.' },
  { id: 'sa-2', domain: DOMAINS.SELF_AWARENESS, axis: AXIS.CARE,
    prompt: 'I notice when I’m so focused on the outcome that I’ve stopped checking how the people involved are doing.' },
  { id: 'sa-3', domain: DOMAINS.SELF_AWARENESS, axis: null,
    prompt: 'Before reacting to something frustrating, I notice what I’m about to say before I say it.' },
  { id: 'sa-4', domain: DOMAINS.SELF_AWARENESS, axis: AXIS.ACCOUNTABILITY,
    prompt: 'I follow through on a commitment even once it becomes inconvenient.' },
  { id: 'sa-5', domain: DOMAINS.SELF_AWARENESS, axis: AXIS.CARE,
    prompt: 'I ask how a decision of mine actually landed for the people affected, rather than assuming I already know.' },
  { id: 'sa-6', domain: DOMAINS.SELF_AWARENESS, axis: null,
    prompt: 'I notice when I’ve mentally checked out of a conversation, and bring my attention back rather than just nodding along.' },
  { id: 'sa-7', domain: DOMAINS.SELF_AWARENESS, axis: AXIS.ACCOUNTABILITY,
    prompt: 'When something isn’t working, I check the process before concluding a person is the problem.' },
  { id: 'sa-8', domain: DOMAINS.SELF_AWARENESS, axis: AXIS.CARE,
    prompt: 'I notice when someone hasn’t spoken up in a while and actually ask why.' },
  { id: 'sa-9', domain: DOMAINS.SELF_AWARENESS, axis: null,
    prompt: 'I can tell the difference between a decision I’m making from genuine judgment and one I’m making from wanting to look decisive.' },

  // --- Ego Patterns ---
  { id: 'eg-1', domain: DOMAINS.EGO_PATTERNS, axis: null,
    prompt: 'When I get feedback that stings, I notice the urge to explain it away before I’ve actually heard it.' },
  { id: 'eg-2', domain: DOMAINS.EGO_PATTERNS, axis: null,
    prompt: 'I can take a piece of critical feedback without needing reassurance immediately afterward.' },
  { id: 'eg-3', domain: DOMAINS.EGO_PATTERNS, axis: null,
    prompt: 'I notice when I’m measuring my own standing against someone else’s instead of just doing the work.' },
  { id: 'eg-4', domain: DOMAINS.EGO_PATTERNS, axis: AXIS.ACCOUNTABILITY,
    prompt: 'I ask whether I actually earned a good outcome, or whether circumstances did most of the work.' },
  { id: 'eg-5', domain: DOMAINS.EGO_PATTERNS, axis: null,
    prompt: 'I can hear "the plan you built isn’t working" without hearing "you failed."' },
  { id: 'eg-6', domain: DOMAINS.EGO_PATTERNS, axis: AXIS.ACCOUNTABILITY,
    prompt: 'When I want to be right in an argument, I notice that wanting before I act on it.' },
  { id: 'eg-7', domain: DOMAINS.EGO_PATTERNS, axis: null,
    prompt: 'I don’t need my ideas to be visibly credited to feel like they mattered.' },
  { id: 'eg-8', domain: DOMAINS.EGO_PATTERNS, axis: AXIS.CARE,
    prompt: 'I can genuinely be glad about someone else’s success without it costing me anything.' },
  { id: 'eg-9', domain: DOMAINS.EGO_PATTERNS, axis: null,
    prompt: 'I can consider that a role or a title I hold isn’t the same thing as who I am.' },

  // --- Conflict Response ---
  { id: 'cr-1', domain: DOMAINS.CONFLICT_RESPONSE, axis: AXIS.CARE,
    prompt: 'I raise a disagreement directly instead of going quiet and hoping it resolves itself.' },
  { id: 'cr-2', domain: DOMAINS.CONFLICT_RESPONSE, axis: null,
    prompt: 'I notice when I’m arguing to win rather than to actually understand the other side.' },
  { id: 'cr-3', domain: DOMAINS.CONFLICT_RESPONSE, axis: AXIS.ACCOUNTABILITY,
    prompt: 'When I disagree with someone, I say so to them directly rather than to someone else about them.' },
  { id: 'cr-4', domain: DOMAINS.CONFLICT_RESPONSE, axis: null,
    prompt: 'When a conversation is getting heated, I can name that out loud and suggest picking it up later, rather than pushing through.' },
  { id: 'cr-5', domain: DOMAINS.CONFLICT_RESPONSE, axis: AXIS.ACCOUNTABILITY,
    prompt: 'I don’t let a small disagreement sit unaddressed until it becomes a bigger one.' },
  { id: 'cr-6', domain: DOMAINS.CONFLICT_RESPONSE, axis: null,
    prompt: 'I can concede a point that’s actually right, even when it’s not the point I wanted to win.' },
  { id: 'cr-7', domain: DOMAINS.CONFLICT_RESPONSE, axis: AXIS.CARE,
    prompt: 'I ask what’s driving someone’s position before assuming I already know.' },
  { id: 'cr-8', domain: DOMAINS.CONFLICT_RESPONSE, axis: null,
    prompt: 'I notice the difference between genuinely needing space to think and just avoiding the conversation.' },
  { id: 'cr-9', domain: DOMAINS.CONFLICT_RESPONSE, axis: AXIS.ACCOUNTABILITY,
    prompt: 'I set a specific time to revisit a disagreement I’ve paused, rather than letting it quietly disappear.' },
]);

export const STRENGTH_TEXT = Object.freeze({
  'sa-1': 'You state clearly what a good outcome looks like before work starts.',
  'sa-2': 'You catch yourself when outcome-focus has crowded out checking on the people involved.',
  'sa-3': 'You create a real pause before responding when something frustrates you.',
  'sa-4': 'You follow through on commitments even once they stop being convenient.',
  'sa-5': 'You actively check how your decisions actually land for the people affected.',
  'sa-6': 'You notice when you’ve checked out of a conversation and bring yourself back.',
  'sa-7': 'You check the process before concluding a person is the problem.',
  'sa-8': 'You notice when someone has gone quiet and actually ask why.',
  'sa-9': 'You can tell genuine judgment apart from the urge to look decisive.',
  'eg-1': 'You notice the urge to explain feedback away before you act on it.',
  'eg-2': 'You can take critical feedback without needing immediate reassurance.',
  'eg-3': 'You notice when you’re comparing your standing instead of just doing the work.',
  'eg-4': 'You check whether you earned a good outcome or circumstances did.',
  'eg-5': 'You can hear that a plan isn’t working without hearing that you failed.',
  'eg-6': 'You notice the urge to be right before you act on it.',
  'eg-7': 'You don’t need visible credit for an idea to feel it mattered.',
  'eg-8': 'You can be genuinely glad about someone else’s success.',
  'eg-9': 'You can separate a role or title you hold from who you are.',
  'cr-1': 'You raise disagreement directly instead of going quiet.',
  'cr-2': 'You notice when you’re arguing to win rather than to understand.',
  'cr-3': 'You say your disagreement to the person, not about them.',
  'cr-4': 'You can name a heated moment out loud and suggest a pause.',
  'cr-5': 'You address small disagreements before they compound.',
  'cr-6': 'You can concede a point that’s actually right, even mid-argument.',
  'cr-7': 'You ask what’s driving someone’s position before assuming you know.',
  'cr-8': 'You can tell needing space apart from avoiding the conversation.',
  'cr-9': 'You set a specific time to revisit a paused disagreement.',
});

export const GROWTH_TEXT = Object.freeze({
  'sa-1': 'A concrete next step: before your next task kicks off, write one sentence defining what "good" looks like.',
  'sa-2': 'A concrete next step: partway through your next results-focused push, ask one person how they’re actually doing.',
  'sa-3': 'A concrete next step: the next time something frustrates you, try counting one breath before you speak.',
  'sa-4': 'A concrete next step: when a commitment becomes inconvenient, say so early rather than letting it quietly slip.',
  'sa-5': 'A concrete next step: pick one recent decision and ask the person it affected how it actually landed.',
  'sa-6': 'A concrete next step: the next time your attention drifts in a conversation, name it silently to yourself and refocus on the actual words being said.',
  'sa-7': 'A concrete next step: before your next skill-gap conclusion, walk the actual process yourself once.',
  'sa-8': 'A concrete next step: name one person who’s been quiet lately and ask them directly for their view.',
  'sa-9': 'A concrete next step: before your next quick call, ask "would I make this same choice unobserved?"',
  'eg-1': 'A concrete next step: next time feedback stings, wait a full ten seconds before responding at all.',
  'eg-2': 'A concrete next step: after your next piece of critical feedback, sit with it for an hour before seeking reassurance.',
  'eg-3': 'A concrete next step: the next time comparison shows up, name it silently to yourself as it happens.',
  'eg-4': 'A concrete next step: after your next win, write one sentence on what circumstances contributed.',
  'eg-5': 'A concrete next step: next time a plan gets criticized, repeat back the critique before responding to it.',
  'eg-6': 'A concrete next step: in your next disagreement, silently ask "do I want to be right, or to be accurate?"',
  'eg-7': 'A concrete next step: let one idea go uncredited this week and notice what that actually costs you.',
  'eg-8': 'A concrete next step: next time a peer succeeds, tell them so directly, in one specific sentence.',
  'eg-9': 'A concrete next step: write one sentence about who you are that has nothing to do with your role.',
  'cr-1': 'A concrete next step: name one disagreement you’ve been sitting on and raise it this week.',
  'cr-2': 'A concrete next step: in your next disagreement, ask yourself once "am I trying to win or to understand?"',
  'cr-3': 'A concrete next step: the next time you disagree with someone, say it to them before you say it to anyone else.',
  'cr-4': 'A concrete next step: write down the sentence you’d use to name a pause, before your next tense conversation.',
  'cr-5': 'A concrete next step: raise the smallest live disagreement you’re currently avoiding, this week.',
  'cr-6': 'A concrete next step: in your next disagreement, look specifically for the one point where they’re right.',
  'cr-7': 'A concrete next step: before your next pushback, ask what’s driving the other position first.',
  'cr-8': 'A concrete next step: next time you want to pause a conversation, name a specific time you’ll return to it.',
  'cr-9': 'A concrete next step: find one disagreement you paused and put a specific date on revisiting it.',
});

export const UNIFORM_TEXT = Object.freeze({
  [DOMAINS.SELF_AWARENESS]: 'Your answers here were consistent across the board - there’s no single standout item to highlight yet. Revisiting this snapshot after trying a few specific practices may surface more contrast.',
  [DOMAINS.EGO_PATTERNS]: 'Your answers here were consistent across the board - there’s no single standout item to highlight yet. Revisiting this snapshot after trying a few specific practices may surface more contrast.',
  [DOMAINS.CONFLICT_RESPONSE]: 'Your answers here were consistent across the board - there’s no single standout item to highlight yet. Revisiting this snapshot after trying a few specific practices may surface more contrast.',
});

// Shown instead of an affirmative strength when even the highest-scoring item
// in a domain was still only "Rarely or never" or "Occasionally" (a value
// below 3). Without this floor, the tool named a "strength" no matter how low
// every answer in the domain was - the highest of several low scores still
// got an unqualified "You consistently do X"-style sentence, which reads as
// flattering rather than accurate for a genuinely infrequent behavior. The
// growth suggestion for the lowest-scoring item is still shown alongside
// this - only the strength claim is suppressed, not the domain's own actionable step.
export const LOW_FLOOR_TEXT = Object.freeze({
  [DOMAINS.SELF_AWARENESS]: 'Across this domain, even your most frequent behavior was still only occasional - there\'s real room to build toward a consistent practice here.',
  [DOMAINS.EGO_PATTERNS]: 'Across this domain, even your most frequent behavior was still only occasional - there\'s real room to build toward a consistent practice here.',
  [DOMAINS.CONFLICT_RESPONSE]: 'Across this domain, even your most frequent behavior was still only occasional - there\'s real room to build toward a consistent practice here.',
});
export const LOW_FLOOR_THRESHOLD = 3; // below "Sometimes" - see LOW_FLOOR_TEXT's comment

// Appended when the named strength and/or growth item was not a unique
// highest/lowest answer. The selection itself is deterministic (first match in
// ITEMS order), but silently presenting one of several equally-scored items as
// "the behavior you reported most often" overstates what the answers actually
// show. Naming the tie is the honest alternative to either hiding it or
// arbitrarily promoting one item over its equals.
export const TIE_NOTE_TEXT = Object.freeze({
  strength: 'Other behaviors in this domain scored just as high - this is one of several at that level, not a single standout.',
  growth: 'Other behaviors in this domain scored just as low - this is one of several at that level, not a single weakest point.',
  both: 'Other behaviors in this domain scored just as high, and others just as low - each named item is one of several at its level rather than a single standout.',
});

// Shown alongside the quadrant name when both axis averages land close to
// the scale's own midpoint - see snapshot-model.js's buildOverlay for why a
// fixed four-quadrant split has nowhere else for a genuinely neutral
// profile to go. The classification itself is unchanged; this only adds
// honest context for reading it.
export const WEAK_SIGNAL_TEXT =
  'Both today’s care and accountability answers sat close to the middle of the scale - this label names which ' +
  'way they leaned, but the lean itself was weak. Treat it as a starting point to revisit, not a strong signal.';

export const QUADRANT_TEXT = Object.freeze({
  'compassionate-accountability': {
    name: 'Compassionate Accountability',
    description: 'Today’s answers describe a pattern that holds a clear standard while also staying attentive to the person meeting it - the combination this framework treats as the target, not a fixed trait you either have or don’t.',
  },
  'protective-indulgence': {
    name: 'Protective Indulgence',
    description: 'Today’s answers describe a pattern that leans on empathy but lets standards slide when holding them would be uncomfortable - understanding without enough follow-through to back it up.',
  },
  'coercive-pressure': {
    name: 'Coercive Pressure',
    description: 'Today’s answers describe a pattern that holds a firm line on standards but with less room for listening first - pressure without enough care behind it.',
  },
  neglect: {
    name: 'Neglect',
    description: 'Today’s answers describe a pattern where standards and follow-through both go soft - problems tend to drift until they become unavoidable.',
  },
});

export const EDUCATIONAL_DISCLAIMER =
  'This is an original, educational self-reflection tool, not a validated psychological assessment. ' +
  'It has not been through expert review, cognitive testing, pilot testing, or reliability analysis. ' +
  'See the public validation roadmap for what that would actually require.';
